import React, { useMemo, useState } from "react";
import { useFieldMappings } from "./hooks/useFieldMappings";
import { AvailableColumnsRail } from "./components/AvailableColumnsRail";
import { MappedFieldsList } from "./components/MappedFieldsList";
import { AddCustomFieldButton } from "./components/AddCustomFieldButton";
import { createJob, updateJob } from "./services/exportJobsService";
import { CredentialSecretField } from "./components/CredentialSecretField";
import { TextField, TransportFields, inputStyle, labelStyle } from "./components/TransportFields";
import { useModalMotion } from "../../components/motion/useModalMotion";
import { ModalShell } from "../../components/motion/ModalShell";

const CJ_TEST_ENDPOINT = "https://tracking.api.cj.com/graphqltest";
const RAKUTEN_O2O_FORMAT = "rakuten_o2o";
const SAVE_ERROR_ID = "export-job-save-error";

// Deep-merges a template's base_destination_config.production into a fresh
// production block — the template's values pre-fill, but nothing here is
// ever allowed to carry a credential_secret_id or credential_injection
// (templates never store either; see 073's comment on
// export_templates.base_destination_config). Both must be stripped together:
// migration 055's validate_destination_config() requires them to be both
// present or both absent for http_get/http_post, so seeding one without the
// other produces a row that fails to save with an opaque error.
//
// host_key and allow_legacy_rsa_host_key are stripped for the same reason
// credentials are, but for a different failure mode: SFTP host-key trust
// (082/DEV-2267 follow-up) is meant to live ONCE per partner, on the
// template's base_destination_config.production — the cycle-scheduled-tasks
// worker now falls back to the template's host_key/allow_legacy_rsa_host_key
// when a job's own destination_config lacks them and host+port match. If a
// new job seeded these fields from the template, that job would carry its
// own copy, and rotating a partner's host key (or flipping the legacy-RSA
// flag) would again mean editing every job instead of one template row —
// exactly the N-row problem this change exists to remove. file_format
// (e.g. rakuten_o2o) is stripped for the same reason: the worker falls back
// to the template's value, so the format lives once per partner.
function seedProductionFromTemplate(template) {
  const { credential_secret_id, credential_injection, host_key, allow_legacy_rsa_host_key, file_format, ...rest } =
    template?.base_destination_config?.production || {};
  return { ...rest };
}

export function seedDestConfigFromTemplate(template) {
  return { transport_type: template?.transport_type, production: seedProductionFromTemplate(template) };
}

export function ExportJobMappingModal({ advertiserId, job = null, templates = [], onClose, onSaved }) {
  // For an EXISTING job, never fall back to templates[0] — that list only
  // contains active templates, and job.export_templates comes back null
  // when the job's own (possibly now-inactive) template can't be read
  // (RLS-blocked, or genuinely missing). Substituting an unrelated
  // template here would let an unrelated field change silently re-point
  // the job at the wrong platform on save. templateLoadFailed below blocks
  // editing in that case instead.
  const template = job ? (job.export_templates ?? null) : (templates[0] ?? null);
  const templateLoadFailed = !!job && !template;
  const [selectedTemplateId, setSelectedTemplateId] = useState(job?.template_id ?? templates[0]?.id ?? null);
  const activeTemplate = job ? template : templates.find((t) => t.id === selectedTemplateId) || template;
  const transportType = activeTemplate?.transport_type;

  const { mappings, resetMappings, addColumnMapping, addCustomField, removeMapping, updateMapping, errors, isValid, toPersistShape } =
    useFieldMappings(job?.field_mappings ?? activeTemplate?.base_field_mappings ?? []);

  const [destConfig, setDestConfig] = useState(() => {
    // An existing job's saved destination_config always wins — never
    // overwritten by the template's base_destination_config, even if the
    // job is later re-pointed at a template with defaults configured.
    if (job?.destination_config) return job.destination_config;
    // New job: seed production fields from the selected template's
    // base_destination_config (e.g. a partner's global endpoint) so the
    // advertiser doesn't have to re-type values support already knows.
    // Anything the advertiser types afterward simply overwrites this seed
    // via updateProduction()'s normal merge.
    return seedDestConfigFromTemplate(activeTemplate);
  });
  const [testEnabled, setTestEnabled] = useState(!!job?.destination_config?.test);
  // The Rakuten O2O file is named after the advertiser's Rakuten MID, so
  // those jobs need one per environment block (see TransportFields). Same
  // rule as the cycle-scheduled-tasks worker: the job's file_format, else
  // the template's production file_format.
  const fileFormat = destConfig.production?.file_format ?? activeTemplate?.base_destination_config?.production?.file_format;
  const needsRakutenMid = transportType === "sftp" && fileFormat === RAKUTEN_O2O_FORMAT;
  const [saveError, setSaveError] = useState(null);
  // The environment block ("production" / "test") whose MID failed the last
  // save, so its input can be marked invalid and tied to the save error.
  const [invalidMidEnv, setInvalidMidEnv] = useState(null);
  const [saving, setSaving] = useState(false);
  const motion = useModalMotion(onClose, { dismissible: !saving });
  const { requestClose } = motion;

  // New-job-only: mappings/destination config/test state only ever
  // initialize once (useState's initializer runs on mount, not on every
  // render), so switching templates via the picker below left the previous
  // template's field mappings and destination_config.transport_type
  // attached — saving submitted a mismatched template_id/transport_type
  // pair the validate_destination_config() trigger then rejected. Selecting
  // a new template now explicitly resets all three.
  const handleTemplateChange = (newTemplateId) => {
    setSelectedTemplateId(newTemplateId);
    const newTemplate = templates.find((t) => t.id === newTemplateId);
    resetMappings(newTemplate?.base_field_mappings ?? []);
    setDestConfig(seedDestConfigFromTemplate(newTemplate));
    setTestEnabled(false);
    setSaveError(null);
    setInvalidMidEnv(null);
  };

  const [wide, setWide] = useState(typeof window !== "undefined" ? window.innerWidth >= 768 : true);
  React.useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [mobilePickerOpen, setMobilePickerOpen] = useState(false);

  const availableColumns = activeTemplate?.available_columns ?? [];
  const mappedSourceColumns = useMemo(
    () => new Set(mappings.filter((m) => m.type === "column").map((m) => m.source_column)),
    [mappings]
  );

  const updateProduction = (patch) => setDestConfig((prev) => ({ ...prev, production: { ...prev.production, ...patch } }));
  const updateTest = (patch) => setDestConfig((prev) => ({ ...prev, test: { ...(prev.test || {}), ...patch } }));

  const handleSave = async () => {
    setSaveError(null);
    setInvalidMidEnv(null);
    if (!activeTemplate) { setSaveError("Select a template."); return; }
    if (!isValid) { setSaveError("Fix field mapping errors before saving."); return; }

    // transportType (the active template's transport) must win — destConfig
    // can carry a stale transport_type from a job loaded under a different
    // template before this modal's state settles, so it's spread first.
    let finalDestConfig = { ...destConfig, transport_type: transportType };
    if (transportType === "cj_graphql") {
      // CJ's test path is fixed and not user-configured — auto-populate it
      // so the row satisfies validate_destination_config()'s optional-test
      // shape without a separate UI section.
      finalDestConfig = {
        ...finalDestConfig,
        test: {
          test_endpoint: CJ_TEST_ENDPOINT,
          credential_secret_id: finalDestConfig.production?.credential_secret_id,
        },
      };
    } else if (!testEnabled) {
      const { test, ...rest } = finalDestConfig;
      finalDestConfig = rest;
    }

    if (needsRakutenMid) {
      for (const env of ["production", ...(finalDestConfig.test ? ["test"] : [])]) {
        const mid = String(finalDestConfig[env]?.mid ?? "").trim();
        if (!/^[0-9]+$/.test(mid)) {
          setSaveError("Enter the advertiser's Rakuten MID (digits only).");
          setInvalidMidEnv(env);
          return;
        }
        finalDestConfig = { ...finalDestConfig, [env]: { ...finalDestConfig[env], mid } };
      }
    }

    const payload = {
      advertiser_id: advertiserId,
      template_id: activeTemplate.id,
      field_mappings: toPersistShape(),
      destination_config: finalDestConfig,
    };

    setSaving(true);
    const { error } = job ? await updateJob(job.id, payload) : await createJob(payload);
    setSaving(false);
    if (error) { setSaveError(error.message); return; }
    onSaved?.();
    requestClose();
  };

  return (
    <ModalShell motion={motion} labelledBy="export-job-mapping-title" maxWidth={860} panelStyle={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "22px 24px 16px", borderBottom: "1px solid #F0F0F0", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div id="export-job-mapping-title" style={labelStyle}>{job ? "Edit Export Job" : "New Export Job"}</div>
            <div style={{ fontWeight: 600, color: "#151515", fontSize: 18, fontFamily: "'Clash Display Variable','Clash Display',sans-serif" }}>
              {activeTemplate?.platform_name || "Select a platform"}
            </div>
          </div>
          <button onClick={requestClose} aria-label="Close" style={{ background: "none", border: "none", color: "#626262", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
          {templateLoadFailed ? (
            <div style={{ padding: 12, background: "#FFF5F5", border: "1px solid #fca5a5", borderRadius: 10, color: "#e53935", fontSize: 13 }}>
              This job's platform template couldn't be loaded — it may have been deactivated. Editing is disabled to avoid accidentally re-pointing this job at a different platform. Contact support if this job needs changes.
            </div>
          ) : (
          <>
          {!job && templates.length > 1 && (
            <div style={{ marginBottom: 20 }}>
              <div style={labelStyle}>Platform Template</div>
              <select
                value={selectedTemplateId ?? ""}
                onChange={(e) => handleTemplateChange(e.target.value)}
                style={{ ...inputStyle, paddingRight: 32 }}
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.platform_name}</option>
                ))}
              </select>
            </div>
          )}

          {saveError && (
            <div id={SAVE_ERROR_ID} style={{ padding: 12, marginBottom: 16, background: "#FFF5F5", border: "1px solid #fca5a5", borderRadius: 10, color: "#e53935", fontSize: 13 }}>{saveError}</div>
          )}

          <div style={{ fontSize: 15, fontWeight: 600, color: "#151515", marginBottom: 12, fontFamily: "'Clash Display Variable','Clash Display',sans-serif" }}>Field Mapping</div>

          {wide ? (
            <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 24, marginBottom: 24 }}>
              <AvailableColumnsRail columns={availableColumns} onAddColumn={addColumnMapping} mappedSourceColumns={mappedSourceColumns} />
              <div>
                <MappedFieldsList mappings={mappings} errors={errors} onUpdate={updateMapping} onRemove={removeMapping} onDropColumn={addColumnMapping} />
                <AddCustomFieldButton onClick={addCustomField} />
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 24 }}>
              <MappedFieldsList mappings={mappings} errors={errors} onUpdate={updateMapping} onRemove={removeMapping} onDropColumn={addColumnMapping} />
              <button
                type="button"
                onClick={() => setMobilePickerOpen(true)}
                style={{ marginTop: 12, padding: "9px 16px", background: "#F90091", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", width: "100%" }}
              >
                + Add field
              </button>
              <AddCustomFieldButton onClick={addCustomField} />
              {mobilePickerOpen && (
                <div style={{ marginTop: 12, border: "1px solid #E2E2E2", borderRadius: 10, padding: 12 }}>
                  <AvailableColumnsRail
                    columns={availableColumns}
                    mappedSourceColumns={mappedSourceColumns}
                    onAddColumn={(col) => { addColumnMapping(col); setMobilePickerOpen(false); }}
                  />
                  <button type="button" onClick={() => setMobilePickerOpen(false)} style={{ marginTop: 10, background: "none", border: "none", color: "#7b7b7b", fontSize: 12, cursor: "pointer" }}>Close</button>
                </div>
              )}
            </div>
          )}

          <div style={{ fontSize: 15, fontWeight: 600, color: "#151515", marginBottom: 12, fontFamily: "'Clash Display Variable','Clash Display',sans-serif" }}>Destination</div>

          {transportType === "cj_graphql" ? (
            <div>
              <TextField label="CJ Account / Website ID" value={destConfig.account_id} onChange={(v) => setDestConfig((prev) => ({ ...prev, account_id: v }))} placeholder="1234567" />
              <TextField label="Mutation Endpoint" value={destConfig.production?.mutation_endpoint} onChange={(v) => updateProduction({ mutation_endpoint: v })} placeholder="https://tracking.api.cj.com/graphql" />
              <CredentialSecretField
                credentialSecretId={destConfig.production?.credential_secret_id}
                onCredentialCreated={(id) => updateProduction({ credential_secret_id: id })}
                description={`cj_graphql / ${activeTemplate?.platform_name ?? "export job"}`}
                advertiserId={advertiserId}
              />
              <div style={{ fontSize: 11, color: "#7b7b7b" }}>Test sends always route to CJ's fixed /graphqltest sandbox and reuse this same credential — no separate configuration needed.</div>
            </div>
          ) : (
            <>
              <div style={{ border: "1px solid #E2E2E2", borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#151515", marginBottom: 10 }}>Production <span style={{ color: "#e53935" }}>*</span></div>
                <TransportFields
                  transportType={transportType}
                  values={destConfig.production || {}}
                  onChange={updateProduction}
                  credentialDescription={`${transportType} / production / ${activeTemplate?.platform_name ?? "export job"}`}
                  showRakutenMid={needsRakutenMid}
                  midErrorId={invalidMidEnv === "production" ? SAVE_ERROR_ID : undefined}
                  advertiserId={advertiserId}
                />
              </div>

              <div style={{ border: `1px solid ${testEnabled ? "#F90091" : "#E2E2E2"}`, borderRadius: 12, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: testEnabled ? 10 : 0 }}>
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#151515" }}>Test</span>
                    <span style={{ fontSize: 11, color: "#7b7b7b", marginLeft: 8 }}>Optional — required before "Send Test" can run</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setTestEnabled((v) => !v)}
                    style={{ padding: "5px 12px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", border: `1px solid ${testEnabled ? "#F90091" : "#E2E2E2"}`, background: testEnabled ? "#FFE9F6" : "#FAFAFA", color: testEnabled ? "#F90091" : "#626262" }}
                  >
                    {testEnabled ? "Configured" : "Configure Test"}
                  </button>
                </div>
                {testEnabled && (
                  <TransportFields
                    transportType={transportType}
                    values={destConfig.test || {}}
                    onChange={updateTest}
                    credentialDescription={`${transportType} / test / ${activeTemplate?.platform_name ?? "export job"}`}
                    showRakutenMid={needsRakutenMid}
                    midErrorId={invalidMidEnv === "test" ? SAVE_ERROR_ID : undefined}
                    advertiserId={advertiserId}
                  />
                )}
              </div>
            </>
          )}
          </>
          )}
        </div>

        <div style={{ padding: "16px 24px", borderTop: "1px solid #F0F0F0", flexShrink: 0, display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={requestClose} style={{ padding: "9px 20px", background: "#FAFAFA", color: "#626262", border: "1px solid #E2E2E2", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer" }}>Cancel</button>
          {!templateLoadFailed && (
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ padding: "9px 20px", background: saving ? "#F9009199" : "#F90091", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: saving ? "default" : "pointer" }}
            >
              {saving ? "Saving…" : job ? "Save Changes" : "Create Export Job"}
            </button>
          )}
        </div>
    </ModalShell>
  );
}
